import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchProducts } from '../../redux/actions/productActions';
import { Button, Table, message, Input, Tag, Tooltip } from 'antd';
import { HistoryOutlined, TagsOutlined, LockOutlined, EditOutlined, CheckOutlined, BarChartOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import { fetchProductPriceHistoryRequest } from '../../redux/actions/priceHistoryActions';
import ProductPriceHistory, { PriceTrendBadge } from './ProductPriceHistory';
import './Product.css';

const { Search } = Input;

const ProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, loading, error } = useSelector((state) => state.products);
  const permissions = useSelector(state => state.auth.permissions || []);
  const [searchText, setSearchText] = useState('');
  const [historyProduct, setHistoryProduct] = useState(null);

  const { productHistory } = useSelector((s) => s.priceHistory || {});
  const canViewPerf = hasPermission(permissions, 'purchase_order/view');

  const handleViewHistory = (product) => {
    setHistoryProduct(product);
    dispatch(fetchProductPriceHistoryRequest(product.id));
  };

  // Check if user has permission to view the grid
  const canViewGrid = hasPermission(permissions, 'product/gridView');
  const canCreate = hasPermission(permissions, 'product/create');
  const canEdit = hasPermission(permissions, 'product/edit');

  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchProducts());
    }
  }, [dispatch, canViewGrid]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleEdit = (productId) => {
    navigate(`/products/edit/${productId}`);
  };

  // Filter products based on search
  const filteredProducts = products.filter(product =>
    product.product_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchText.toLowerCase()) ||
    product.hsn_sac_code?.toLowerCase().includes(searchText.toLowerCase())
  );

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">
            You don't have permission to view products.
          </div>
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      fixed: 'left',
      ellipsis: true,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 160,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <strong>{text}</strong>
        </Tooltip>
      ),
    },
    {
      title: 'HSN/SAC',
      dataIndex: 'hsn_sac_code',
      key: 'hsn_sac_code',
      width: 95,
      render: (text) => <Tag color="geekblue">{text || '—'}</Tag>,
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 65,
      align: 'center',
    },
    {
      title: 'Tax',
      dataIndex: 'tax_slab',
      key: 'tax_slab',
      width: 65,
      align: 'center',
      render: (text) => <Tag color="green">{text}%</Tag>,
    },
    {
      title: 'Rate',
      dataIndex: 'purchase_rate',
      key: 'purchase_rate',
      width: 115,
      align: 'right',
      render: (text, record) => (
        <span>
          <strong style={{ color: '#28a745' }}>₹{text}</strong>
          <PriceTrendBadge history={productHistory?.[record.id]} />
        </span>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 80,
      align: 'center',
      render: (text) => <Tag color="orange">{text}%</Tag>,
    },
    {
      title: 'Vendors',
      dataIndex: 'vendors',
      key: 'vendors',
      width: 150,
      render: (vendors) =>
        vendors && vendors.length > 0
          ? vendors.slice(0, 2).map(v => <Tag key={v.id} color="blue" style={{ marginBottom: 2 }}>{v.firm_name}</Tag>)
            .concat(vendors.length > 2 ? [<Tag key="more">+{vendors.length - 2} more</Tag>] : [])
          : <span style={{ color: '#aaa' }}>—</span>,
    },
    ...(canViewPerf ? [{
      title: 'History',
      key: 'price_history',
      align: 'center',
      width: 80,
      render: (_, record) => (
        <Tooltip title="View price change history">
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
          />
        </Tooltip>
      ),
    }] : []),
    ...(canEdit ? [{
      title: 'Action',
      key: 'actions',
      align: 'center',
      width: 75,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record.id)}
        >
          Edit
        </Button>
      ),
    }] : []),
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><TagsOutlined /></div>
            <div className="page-header-text">
              <h1>Product Management</h1>
              <p>Manage your product catalog and inventory</p>
            </div>
          </div>
          <div className="page-header-actions">
            {canCreate && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/products/add')}
              >
                <PlusOutlined />
                Add New Product
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon"><InboxOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{products.length}</div>
              <div className="stat-label">Total Products</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
            <div className="stat-icon" style={{ background: '#d4edda', color: '#28a745' }}><CheckOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{products.filter(p => p.discount > 0).length}</div>
              <div className="stat-label">With Discounts</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#ffc107' }}>
            <div className="stat-icon" style={{ background: '#fff3cd', color: '#856404' }}><BarChartOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{new Set(products.map(p => p.category)).size}</div>
              <div className="stat-label">Categories</div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="modern-table-container">
          <div className="table-header">
            <h2 className="table-title">Products List</h2>
            <Search
              placeholder="Search products..."
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              size="large"
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredProducts}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} products`,
            }}
            locale={{
              emptyText: (
                <div className="empty-state">
                  <div className="empty-state-icon"><InboxOutlined /></div>
                  <div className="empty-state-text">No products found</div>
                  {canCreate && (
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/products/add')}
                    >
                      Add Your First Product
                    </button>
                  )}
                </div>
              ),
            }}
          />
        </div>
      </div>

      {/* Price History Modal */}
      {historyProduct && (
        <ProductPriceHistory
          open={!!historyProduct}
          onClose={() => setHistoryProduct(null)}
          product={historyProduct}
        />
      )}
    </div>
  );
};

export default ProductList;